'use client';

import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Language } from '@/hooks/useLanguageSettings';

type LanguageOption = {
  value: Language;
  label: string;
  flag: string;
};

interface LanguageSelectorProps {
  currentLanguage: Language;
  isLoading: boolean;
  languageOptions: LanguageOption[];
  onSelect: (language: Language) => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  isLoading,
  languageOptions,
  onSelect,
}) => {

  const [isOpen, setIsOpen] = React.useState(false);

  const handleLanguageSelect = (language: Language) => {
    onSelect(language);
    setIsOpen(false);
  };

  const currentOption = languageOptions.find(option => option.value === currentLanguage);

  return (
    <div className="relative">
      {/* Language selector button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex items-center gap-2 w-full sm:w-[180px] justify-between bg-background border-border hover:border-primary/30 hover:bg-muted/30 transition-all duration-200 rounded-lg"
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{currentOption?.flag}</span>
          <span className="text-sm font-medium">
            {currentOption?.label}
          </span>
        </div>
        <ChevronDown 
          className={`h-3 w-3 text-muted-foreground transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </Button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown content */}
          <div className="absolute top-full left-0 mt-2 w-full sm:w-[180px] bg-background border border-border rounded-lg shadow-xl z-50 overflow-hidden animate-in slide-in-from-top-2 duration-200">
            {languageOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleLanguageSelect(option.value)}
                disabled={isLoading}
                className={`w-full px-3 py-2.5 text-left flex items-center justify-between hover:bg-muted/50 transition-colors duration-150 ${
                  currentLanguage === option.value ? 'bg-primary/5' : ''
                }`}
                aria-label={`Select ${option.label}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">{option.flag}</span>
                  <span className="text-sm font-medium text-foreground">
                    {option.label}
                  </span>
                </div>
                {currentLanguage === option.value && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
